export type Language = 'en' | 'it';

export interface Strings {
  // tabs
  tabAlarm: string;
  tabGateways: string;
  tabCameras: string;
  // gateways / nodes
  gateways: string;
  nodes: string;
  gatewayNameLabel: string;
  gatewayUrlLabel: string;
  gatewayUrlPlaceholder: string;
  nodeDefaultLabel: (index: number) => string;
  nodeDefaultDescription: (index: number) => string;
  statusUnknown: string;
  statusOn: string;
  statusOff: string;
  errorNoUrl: string;
  errorHttp: (status: number) => string;
  errorConnection: string;
  // alarm
  alarmTitle: string;
  alarmDescription: string;
  alarmStatusLabel: string;
  alarmStatusUnknown: string;
  alarmStatusArmed: string;
  alarmStatusDisarmed: string;
  alarmArm: string;
  alarmDisarm: string;
  alarmAuthTitle: string;
  alarmAuthConfirm: string;
  alarmNotificationsEnabled: string;
  alarmNotificationsDisabled: string;
  // node edit
  nodeEditTitle: string;
  nodeEditName: string;
  nodeEditDescription: string;
  nodeEditType: string;
  nodeEditSave: string;
  nodeEditCancel: string;
  // node types
  nodeTypeGeneric: string;
  nodeTypeThiefAlert: string;
  nodeTypeDoorLock: string;
  nodeTypeLight: string;
  nodeTypeAirConditioner: string;
  nodeTypeBoiler: string;
  nodeTypeShutter: string;
  nodeTypeFan: string;
  nodeTypeSmartPlug: string;
  nodeTypeVacuum: string;
  nodeTypeTv: string;
  // command error
  cmdNotSupported: string;
  commandSent: string;
  // AC card
  acTemperature: string;
  acMode: string;
  acModeCool: string;
  acModeHeat: string;
  acModeAuto: string;
  acModeDry: string;
  acModeFan: string;
  acFanSpeed: string;
  acFanAuto: string;
  acFanLow: string;
  acFanMedium: string;
  acFanHigh: string;
  acApply: string;
  // light card
  lightBrightness: string;
  lightColorTemp: string;
  lightColorWarm: string;
  lightColorCool: string;
  // shutter card
  shutterOpen: string;
  shutterClose: string;
  shutterStop: string;
  shutterPosition: string;
  // boiler card
  boilerTargetTemp: string;
  // fan card
  fanSpeed: string;
  fanOscillation: string;
  // smart plug card
  smartPlugPower: string;
  smartPlugToday: string;
  // vacuum card
  vacuumStart: string;
  vacuumDock: string;
  vacuumPause: string;
  vacuumMode: string;
  vacuumModeAuto: string;
  vacuumModeSpot: string;
  vacuumModeSilent: string;
  vacuumModeMax: string;
  vacuumBattery: string;
  // TV card
  tvVolume: string;
  tvChannel: string;
  tvInput: string;
  // gateway kind
  gatewayKindLabel: string;
  gatewayKindRelayBoard: string;
  gatewayKindSwitchbot: string;
  // gateway delete
  gatewayDeleteConfirm: string;
  gatewayDeleteButton: string;
  gatewayDeleteOnlyOne: string;
  gatewayStatusConnected: string;
  // settings
  settingsTitle: string;
  settingsExport: string;
  settingsExportDesc: string;
  settingsImport: string;
  settingsImportDesc: string;
  settingsImportSuccess: string;
  settingsImportError: string;
  settingsTheme: string;
  settingsThemeDark: string;
  settingsThemeLight: string;
  settingsLogout: string;
  settingsSwitchHouse: string;
  settingsAddHouse: string;
  settingsSyncFromPi: string;
  settingsSyncFromPiDesc: string;
  settingsReset: string;
  settingsResetDesc: string;
  settingsResetConfirm: string;
  settingsResetButton: string;
  // biometric
  biometricEnable: string;
  biometricEnableDesc: string;
  biometricLoginButton: string;
  biometricDisable: string;
  biometricNotNow: string;
  biometricError: string;
  biometricDontAsk: string;
  biometricDontAskConfirm: string;
  biometricPasswordStale: string;
  // setup
  housePickerTitle: string;
  housePickerAddHouse: string;
  setupTitle: string;
  setupSubtitle: string;
  setupDeviceId: string;
  setupDomain: string;
  setupUsername: string;
  setupPassword: string;
  setupSubmit: string;
  // login
  loginTitle: string;
  loginWelcome: (username: string) => string;
  loginPassword: string;
  loginSubmit: string;
  loginWrongPassword: string;
  loginSwitchHouse: string;
  // validation
  fieldRequired: string;
  // cameras
  camerasNone: string;
  camerasRefresh: string;
  cameraSettings: string;
  cameraSettingRecord: string;
  cameraSettingMotion: string;
  cameraSettingDetect: string;
  cameraSettingSnapshots: string;
  cameraRecord: string;
  cameraRecording: string;
  cameraRecordDuration: string;
  cameraRecordings: string;
  cameraRecordingsNone: string;
  cameraDeleteButton: string;
  cameraDeleteConfirm: string;
  // sensors
  tabSensors: string;
  sensorsEmpty: string;
  sensorsAddSource: string;
  sensorsSourceName: string;
  sensorsSourceUrl: string;
  sensorsLastUpdated: string;
  sensorsRemoveSource: string;
  sensorsAddButton: string;
  sensorsErrorFetch: string;
  sensorHistory: string;
  sensorHistoryNone: string;
  // accessible names for icon-only and non-text controls
  a11yIncrease: (what: string) => string;
  a11yDecrease: (what: string) => string;
  a11yRefresh: string;
  a11yRefreshStatus: string;
  a11yClose: string;
  a11yEditNode: string;
  a11yToggleNode: (name: string) => string;
  a11yOpenGateway: (name: string) => string;
  a11yGatewaySettings: string;
  a11yShowCamera: (name: string) => string;
  a11yEnterFullscreen: string;
  a11yExitFullscreen: string;
  a11yPlayRecording: string;
  a11yDeleteRecording: string;
  a11yPanUp: string;
  a11yPanDown: string;
  a11yPanLeft: string;
  a11yPanRight: string;
  a11yPanStop: string;
}

export const strings: Record<Language, Strings> = {
  en: {
    tabAlarm: 'Alarm',
    tabGateways: 'Gateways',
    tabCameras: 'Cameras',
    gateways: 'Gateways',
    nodes: 'Nodes',
    gatewayNameLabel: 'Name',
    gatewayUrlLabel: 'URL',
    gatewayUrlPlaceholder: 'https://device-001.easyhome.io',
    nodeDefaultLabel: (i) => `Node ${i + 1}`,
    nodeDefaultDescription: (i) => `Description node ${i + 1}`,
    statusUnknown: 'Unknown',
    statusOn: 'On',
    statusOff: 'Off',
    errorNoUrl: 'No URL configured',
    errorHttp: (status) => `Error ${status}`,
    errorConnection: 'Connection failed',
    alarmTitle: 'ALARM',
    alarmDescription: 'Activate or deactivate your home alarm.\nFor security reasons you will be asked to re-enter your password.',
    alarmStatusLabel: 'Status',
    alarmStatusUnknown: 'Unknown',
    alarmStatusArmed: 'Armed',
    alarmStatusDisarmed: 'Disarmed',
    alarmArm: 'ARM',
    alarmDisarm: 'DISARM',
    alarmAuthTitle: 'Confirm',
    alarmAuthConfirm: 'Confirm',
    alarmNotificationsEnabled: 'Notifications Enabled',
    alarmNotificationsDisabled: 'Notifications Disabled',
    nodeEditTitle: 'Edit Node',
    nodeEditName: 'Name',
    nodeEditDescription: 'Description',
    nodeEditType: 'Type',
    nodeEditSave: 'Save',
    nodeEditCancel: 'Cancel',
    nodeTypeGeneric: 'Generic',
    nodeTypeThiefAlert: 'Thief Alert',
    nodeTypeDoorLock: 'Door Lock',
    nodeTypeLight: 'Light',
    nodeTypeAirConditioner: 'Air Conditioner',
    nodeTypeBoiler: 'Boiler',
    nodeTypeShutter: 'Shutter',
    nodeTypeFan: 'Fan',
    nodeTypeSmartPlug: 'Smart Plug',
    nodeTypeVacuum: 'Robot Vacuum',
    nodeTypeTv: 'TV',
    cmdNotSupported: 'Not supported by device',
    commandSent: 'Command sent',
    acTemperature: 'Temp',
    acMode: 'Mode',
    acModeCool: 'Cool',
    acModeHeat: 'Heat',
    acModeAuto: 'Auto',
    acModeDry: 'Dry',
    acModeFan: 'Fan',
    acFanSpeed: 'Fan',
    acFanAuto: 'Auto',
    acFanLow: 'Low',
    acFanMedium: 'Med',
    acFanHigh: 'High',
    acApply: 'Set',
    lightBrightness: 'Brightness',
    lightColorTemp: 'Color',
    lightColorWarm: 'Warm',
    lightColorCool: 'Cool',
    shutterOpen: 'Open',
    shutterClose: 'Close',
    shutterStop: 'Stop',
    shutterPosition: 'Position',
    boilerTargetTemp: 'Target',
    fanSpeed: 'Speed',
    fanOscillation: 'Oscillation',
    smartPlugPower: 'Power',
    smartPlugToday: 'Today',
    vacuumStart: 'Start',
    vacuumDock: 'Dock',
    vacuumPause: 'Pause',
    vacuumMode: 'Mode',
    vacuumModeAuto: 'Auto',
    vacuumModeSpot: 'Spot',
    vacuumModeSilent: 'Silent',
    vacuumModeMax: 'Max',
    vacuumBattery: 'Battery',
    tvVolume: 'Volume',
    tvChannel: 'Channel',
    tvInput: 'Input',
    gatewayKindLabel: 'Board Type',
    gatewayKindRelayBoard: 'Relay Board',
    gatewayKindSwitchbot: 'SwitchBot Hub',
    gatewayDeleteConfirm: 'Delete this gateway?',
    gatewayDeleteButton: 'Delete',
    gatewayDeleteOnlyOne: 'At least one gateway is required',
    gatewayStatusConnected: 'Connected',
    setupTitle: 'Welcome',
    setupSubtitle: 'Configure your first relay board to get started.',
    setupDeviceId: 'Device ID',
    setupDomain: 'Domain',
    setupUsername: 'Username',
    setupPassword: 'Password',
    setupSubmit: 'GET STARTED',
    loginTitle: 'Welcome back',
    loginWelcome: (u) => `Logged in as ${u}`,
    loginPassword: 'Password',
    loginSubmit: 'ENTER',
    loginWrongPassword: 'Wrong password',
    loginSwitchHouse: 'Choose a different house',
    fieldRequired: 'Required',
    settingsTitle: 'Settings',
    settingsExport: 'Export Configuration',
    settingsExportDesc: 'Download your settings as a .conf file (password excluded).',
    settingsImport: 'Import Configuration',
    settingsImportDesc: 'Restore settings from a previously exported .conf file.',
    settingsImportSuccess: 'Configuration imported. Please log in again.',
    settingsImportError: 'Invalid configuration file.',
    settingsTheme: 'Appearance',
    settingsThemeDark: 'Dark',
    settingsThemeLight: 'Light',
    settingsLogout: 'Log out',
    settingsSwitchHouse: 'Switch house',
    settingsAddHouse: 'Add another house',
    settingsSyncFromPi: 'Sync from Device',
    settingsSyncFromPiDesc: 'Reload gateway names and node labels from the Pi.',
    settingsReset: 'Reset App',
    settingsResetDesc: 'Delete all data and return to initial setup.',
    settingsResetConfirm: 'This will permanently delete all settings, gateways and credentials. Are you sure?',
    settingsResetButton: 'Reset',
    biometricEnable: 'Enable biometric unlock',
    biometricEnableDesc: 'Use Face ID, fingerprint, or PIN instead of typing your password.',
    biometricLoginButton: 'Use biometrics',
    biometricDisable: 'Disable biometric unlock',
    biometricNotNow: 'Not now',
    biometricError: 'Biometric authentication failed',
    biometricDontAsk: "Don't ask again",
    biometricDontAskConfirm: 'You can enable biometric unlock at any time from Settings.',
    biometricPasswordStale: 'Password changed. Please log in manually.',
    housePickerTitle: 'Choose a house',
    housePickerAddHouse: '+ Add house',
    camerasNone: 'No cameras found. Check Frigate is reachable.',
    camerasRefresh: 'Refresh',
    cameraSettings: 'Camera Settings',
    cameraSettingRecord: 'Recording',
    cameraSettingMotion: 'Motion Detection',
    cameraSettingDetect: 'AI Detection',
    cameraSettingSnapshots: 'Snapshots',
    cameraRecord: 'Record',
    cameraRecording: 'Recording',
    cameraRecordDuration: 'Record duration (s)',
    cameraRecordings: 'Recordings',
    cameraRecordingsNone: 'No recordings in the last 7 days',
    cameraDeleteButton: 'Delete',
    cameraDeleteConfirm: 'Remove this recording from the list?',
    tabSensors: 'Sensors',
    sensorsEmpty: 'No sensor sources configured.',
    sensorsAddSource: 'Add a sensor source to get started.',
    sensorsSourceName: 'Name',
    sensorsSourceUrl: 'URL',
    sensorsLastUpdated: 'Updated',
    sensorsRemoveSource: 'Remove',
    sensorsAddButton: 'Add Source',
    sensorsErrorFetch: 'Could not fetch sensors',
    sensorHistory: 'History',
    sensorHistoryNone: 'No data yet',
    a11yIncrease: (what) => `Increase ${what}`,
    a11yDecrease: (what) => `Decrease ${what}`,
    a11yRefresh: 'Refresh',
    a11yRefreshStatus: 'Refresh status',
    a11yClose: 'Close',
    a11yEditNode: 'Edit device',
    a11yToggleNode: (name) => `Turn ${name} on or off`,
    a11yOpenGateway: (name) => `Open ${name}`,
    a11yGatewaySettings: 'Gateway settings',
    a11yShowCamera: (name) => `Show ${name}`,
    a11yEnterFullscreen: 'Enter fullscreen',
    a11yExitFullscreen: 'Exit fullscreen',
    a11yPlayRecording: 'Play recording',
    a11yDeleteRecording: 'Delete recording',
    a11yPanUp: 'Pan up',
    a11yPanDown: 'Pan down',
    a11yPanLeft: 'Pan left',
    a11yPanRight: 'Pan right',
    a11yPanStop: 'Stop moving',
  },
  it: {
    tabAlarm: 'Allarme',
    tabGateways: 'Schede',
    tabCameras: 'Telecamere',
    gateways: 'Schede',
    nodes: 'Nodi',
    gatewayNameLabel: 'Nome',
    gatewayUrlLabel: 'URL',
    gatewayUrlPlaceholder: 'https://device-001.easyhome.io',
    nodeDefaultLabel: (i) => `Nodo ${i + 1}`,
    nodeDefaultDescription: (i) => `Descrizione nodo ${i + 1}`,
    statusUnknown: 'Sconosciuto',
    statusOn: 'Acceso',
    statusOff: 'Spento',
    errorNoUrl: 'URL non configurato',
    errorHttp: (status) => `Errore ${status}`,
    errorConnection: 'Connessione fallita',
    alarmTitle: 'ALLARME',
    alarmDescription: "Attiva o disattiva l'allarme della tua casa.\nPer motivi di sicurezza ti verrà chiesto di reinserire la password.",
    alarmStatusLabel: 'Status',
    alarmStatusUnknown: 'Sconosciuto',
    alarmStatusArmed: 'Armato',
    alarmStatusDisarmed: 'Disarmato',
    alarmArm: 'ARMA',
    alarmDisarm: 'DISARMA',
    alarmAuthTitle: 'Conferma',
    alarmAuthConfirm: 'Conferma',
    alarmNotificationsEnabled: 'Notifiche Abilitate',
    alarmNotificationsDisabled: 'Notifiche Disabilitate',
    nodeEditTitle: 'Modifica Nodo',
    nodeEditName: 'Nome',
    nodeEditDescription: 'Descrizione',
    nodeEditType: 'Tipo',
    nodeEditSave: 'Salva',
    nodeEditCancel: 'Annulla',
    nodeTypeGeneric: 'Generico',
    nodeTypeThiefAlert: 'Allarme Ladro',
    nodeTypeDoorLock: 'Serratura',
    nodeTypeLight: 'Luce',
    nodeTypeAirConditioner: 'Condizionatore',
    nodeTypeBoiler: 'Caldaia',
    nodeTypeShutter: 'Tapparella',
    nodeTypeFan: 'Ventilatore',
    nodeTypeSmartPlug: 'Presa Smart',
    nodeTypeVacuum: 'Robot Aspirapolvere',
    nodeTypeTv: 'TV',
    cmdNotSupported: 'Non supportato dal dispositivo',
    commandSent: 'Comando inviato',
    acTemperature: 'Temp',
    acMode: 'Modalità',
    acModeCool: 'Freddo',
    acModeHeat: 'Caldo',
    acModeAuto: 'Auto',
    acModeDry: 'Deumidifica',
    acModeFan: 'Ventola',
    acFanSpeed: 'Ventola',
    acFanAuto: 'Auto',
    acFanLow: 'Bassa',
    acFanMedium: 'Media',
    acFanHigh: 'Alta',
    acApply: 'Imposta',
    lightBrightness: 'Luminosità',
    lightColorTemp: 'Colore',
    lightColorWarm: 'Caldo',
    lightColorCool: 'Freddo',
    shutterOpen: 'Apri',
    shutterClose: 'Chiudi',
    shutterStop: 'Stop',
    shutterPosition: 'Posizione',
    boilerTargetTemp: 'Obiettivo',
    fanSpeed: 'Velocità',
    fanOscillation: 'Oscillazione',
    smartPlugPower: 'Potenza',
    smartPlugToday: 'Oggi',
    vacuumStart: 'Avvia',
    vacuumDock: 'Ricarica',
    vacuumPause: 'Pausa',
    vacuumMode: 'Modalità',
    vacuumModeAuto: 'Auto',
    vacuumModeSpot: 'Spot',
    vacuumModeSilent: 'Silenzioso',
    vacuumModeMax: 'Max',
    vacuumBattery: 'Batteria',
    tvVolume: 'Volume',
    tvChannel: 'Canale',
    tvInput: 'Sorgente',
    gatewayKindLabel: 'Tipo Scheda',
    gatewayKindRelayBoard: 'Scheda Relè',
    gatewayKindSwitchbot: 'SwitchBot Hub',
    gatewayDeleteConfirm: 'Eliminare questo gateway?',
    gatewayDeleteButton: 'Elimina',
    gatewayDeleteOnlyOne: 'È necessario almeno un gateway',
    gatewayStatusConnected: 'Connesso',
    setupTitle: 'Benvenuto',
    setupSubtitle: 'Configura la prima scheda relè per iniziare.',
    setupDeviceId: 'ID Dispositivo',
    setupDomain: 'Dominio',
    setupUsername: 'Nome utente',
    setupPassword: 'Password',
    setupSubmit: 'INIZIA',
    loginTitle: 'Bentornato',
    loginWelcome: (u) => `Connesso come ${u}`,
    loginPassword: 'Password',
    loginSubmit: 'ENTRA',
    loginWrongPassword: 'Password errata',
    loginSwitchHouse: 'Scegli una casa diversa',
    fieldRequired: 'Obbligatorio',
    settingsTitle: 'Impostazioni',
    settingsExport: 'Esporta Configurazione',
    settingsExportDesc: 'Scarica le impostazioni come file .conf (password esclusa).',
    settingsImport: 'Importa Configurazione',
    settingsImportDesc: 'Ripristina le impostazioni da un file .conf esportato in precedenza.',
    settingsImportSuccess: 'Configurazione importata. Effettua nuovamente il login.',
    settingsImportError: 'File di configurazione non valido.',
    settingsTheme: 'Aspetto',
    settingsThemeDark: 'Scuro',
    settingsThemeLight: 'Chiaro',
    settingsLogout: 'Esci',
    settingsSwitchHouse: 'Cambia casa',
    settingsAddHouse: 'Aggiungi un\'altra casa',
    settingsSyncFromPi: 'Sincronizza dal dispositivo',
    settingsSyncFromPiDesc: 'Ricarica nomi gateway e etichette nodi dal Pi.',
    settingsReset: 'Reset App',
    settingsResetDesc: 'Elimina tutti i dati e torna alla configurazione iniziale.',
    settingsResetConfirm: 'Questa operazione eliminerà definitivamente tutte le impostazioni, i gateway e le credenziali. Sei sicuro?',
    settingsResetButton: 'Reset',
    biometricEnable: 'Abilita sblocco biometrico',
    biometricEnableDesc: 'Usa Face ID, impronta digitale o PIN al posto della password.',
    biometricLoginButton: 'Usa biometria',
    biometricDisable: 'Disabilita sblocco biometrico',
    biometricNotNow: 'Non ora',
    biometricError: 'Autenticazione biometrica fallita',
    biometricDontAsk: 'Non chiedere più',
    biometricDontAskConfirm: 'Puoi abilitare lo sblocco biometrico in qualsiasi momento dalle Impostazioni.',
    biometricPasswordStale: 'Password cambiata. Effettua il login manualmente.',
    housePickerTitle: 'Scegli una casa',
    housePickerAddHouse: '+ Aggiungi casa',
    camerasNone: 'Nessuna telecamera trovata. Verifica che Frigate sia raggiungibile.',
    camerasRefresh: 'Aggiorna',
    cameraSettings: 'Impostazioni Telecamera',
    cameraSettingRecord: 'Registrazione',
    cameraSettingMotion: 'Rilevamento Movimento',
    cameraSettingDetect: 'Rilevamento AI',
    cameraSettingSnapshots: 'Istantanee',
    cameraRecord: 'Registra',
    cameraRecording: 'Registrazione',
    cameraRecordDuration: 'Durata registrazione (s)',
    cameraRecordings: 'Registrazioni',
    cameraRecordingsNone: 'Nessuna registrazione negli ultimi 7 giorni',
    cameraDeleteButton: 'Elimina',
    cameraDeleteConfirm: 'Rimuovere questa registrazione dalla lista?',
    tabSensors: 'Sensori',
    sensorsEmpty: 'Nessuna sorgente sensori configurata.',
    sensorsAddSource: 'Aggiungi una sorgente sensori per iniziare.',
    sensorsSourceName: 'Nome',
    sensorsSourceUrl: 'URL',
    sensorsLastUpdated: 'Aggiornato',
    sensorsRemoveSource: 'Rimuovi',
    sensorsAddButton: 'Aggiungi Sorgente',
    sensorsErrorFetch: 'Impossibile recuperare i sensori',
    sensorHistory: 'Storico',
    sensorHistoryNone: 'Nessun dato ancora',
    a11yIncrease: (what) => `Aumenta ${what}`,
    a11yDecrease: (what) => `Riduci ${what}`,
    a11yRefresh: 'Aggiorna',
    a11yRefreshStatus: 'Aggiorna stato',
    a11yClose: 'Chiudi',
    a11yEditNode: 'Modifica dispositivo',
    a11yToggleNode: (name) => `Accendi o spegni ${name}`,
    a11yOpenGateway: (name) => `Apri ${name}`,
    a11yGatewaySettings: 'Impostazioni gateway',
    a11yShowCamera: (name) => `Mostra ${name}`,
    a11yEnterFullscreen: 'Schermo intero',
    a11yExitFullscreen: 'Esci da schermo intero',
    a11yPlayRecording: 'Riproduci registrazione',
    a11yDeleteRecording: 'Elimina registrazione',
    a11yPanUp: 'Ruota in alto',
    a11yPanDown: 'Ruota in basso',
    a11yPanLeft: 'Ruota a sinistra',
    a11yPanRight: 'Ruota a destra',
    a11yPanStop: 'Ferma il movimento',
  },
};
